import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { integrationEnvironmentOptions } from 'constants/loanActionEditor';
import { PAGE_CODES } from 'constants/pageCodes';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import {
  getEnvHelperText,
  getEnvironmentOptionLabel,
  isEnvConfiguredOnPackage,
  isEnvironmentOptionEqual,
} from 'utils/integrationPackageEnv';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getMethodsAction, getPackageAction, getPackagesAction } from 'store/system/actions';
import {
  selectMethodsByPackageId,
  selectPackageById,
  selectPackages,
  selectPackagesLoading,
} from 'store/system/selectors';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import {
  endpointBadgeStyles,
  getVerbBadgeStyles,
  integrationOptionLogoStyles,
  integrationOptionStyles,
  integrationOptionTextStyles,
} from './LoanActionIntegrationRow.styles';
import { wrapperClassname } from './LoanActionItem.constants';

const getPackageLabel = option => option.name;
const getMethodLabel = option => option.name;
// API DTOs key by `id` (normalizr idAttribute), while the form stores the chosen
// ids under callIntegration.packageId / .methodId (the backend executor's contract).
const isPackageEqual = (option, value) => option.id === value.id;
const isMethodEqual = (option, value) => option.id === value.id;

const getPackageInitials = name =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const PackageOptionContent = ({ option }) => {
  const initials = option.name ? getPackageInitials(option.name) : '??';
  const isActive = option.status === 'Active';

  return (
    <Box sx={integrationOptionStyles}>
      <Box sx={integrationOptionLogoStyles}>{initials}</Box>
      <Box sx={integrationOptionTextStyles}>
        <Typography className="truncate" variant="medium14">
          {option.name}
        </Typography>
        {!!option.description && (
          <Typography className="truncate" color="text.secondary" variant="regular12">
            {option.description}
          </Typography>
        )}
      </Box>
      {isActive && <LabelCDS size="S" title="Active" variant="GREEN" />}
    </Box>
  );
};

PackageOptionContent.propTypes = {
  option: PropTypes.shape({
    description: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

const MethodOptionContent = ({ option }) => (
  <Box sx={integrationOptionStyles}>
    <Box sx={integrationOptionTextStyles}>
      <Typography className="truncate" variant="medium14">
        {option.name}
      </Typography>
      {!!option.description && (
        <Typography className="truncate" color="text.secondary" variant="regular12">
          {option.description}
        </Typography>
      )}
    </Box>
  </Box>
);

MethodOptionContent.propTypes = {
  option: PropTypes.shape({
    description: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

const MethodEndpointBadge = ({ verb, path }) => {
  const verbBadgeStyles = getVerbBadgeStyles(verb);

  return (
    <Box sx={endpointBadgeStyles}>
      <Box component="span" sx={verbBadgeStyles}>
        {verb}
      </Box>
      <Box component="span">{path}</Box>
    </Box>
  );
};

MethodEndpointBadge.propTypes = {
  path: PropTypes.string.isRequired,
  verb: PropTypes.string.isRequired,
};

const LoanActionIntegrationRow = ({ rootPath, isDisabled, isEditAllowed }) => {
  const dispatch = useDispatch();
  const { form, setMultipleInForm } = useFormContext();
  const companyId = useCompanyId();

  const { isViewAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.INTEGRATION_STUDIO));

  const packages = useSelector(selectPackages);
  const isPackagesLoading = useSelector(selectPackagesLoading);

  const callIntegration = _.get(form, rootPath) ?? {};
  const selectedPackageId = callIntegration.packageId ?? null;

  const methods = useSelector(selectMethodsByPackageId(selectedPackageId));
  // The selected package's full record (baseUrlDev/Staging/Prod), hydrated by getPackageAction below —
  // used to disable env slots the package hasn't configured.
  const selectedPackageDetail = useSelector(selectPackageById(selectedPackageId));

  const packageOptions = useMemo(() => packages.filter(p => p.status === 'Active'), [packages]);

  const selectedPackage = useMemo(
    () => packageOptions.find(p => p.id === selectedPackageId) ?? null,
    [packageOptions, selectedPackageId],
  );

  const selectedMethod = useMemo(() => {
    const methodId = callIntegration.methodId ?? null;

    return methods.find(m => m.id === methodId) ?? null;
  }, [callIntegration.methodId, methods]);

  const selectedEnvironment = callIntegration.environment ?? null;

  const onPackageChange = useCallback(
    (_, option) => {
      setMultipleInForm({
        [`${rootPath}.packageId`]: option?.id ?? null,
        [`${rootPath}.methodId`]: null,
        // Reset the env — a value valid for the previous package may be an unconfigured slot on the new one.
        [`${rootPath}.environment`]: null,
      });
    },
    [rootPath, setMultipleInForm],
  );

  const onMethodChange = useCallback(
    (_, option) => {
      setMultipleInForm({ [`${rootPath}.methodId`]: option?.id ?? null });
    },
    [rootPath, setMultipleInForm],
  );

  const onEnvironmentChange = useCallback(
    (_, option) => {
      setMultipleInForm({ [`${rootPath}.environment`]: option ?? null });
    },
    [rootPath, setMultipleInForm],
  );

  const isEnvironmentOptionDisabled = useCallback(
    option => !isEnvConfiguredOnPackage(selectedPackageDetail, option.value),
    [selectedPackageDetail],
  );

  useEffectSafeStrictMode(() => {
    if (!companyId || !isViewAllowed) {
      return;
    }

    dispatch(getPackagesAction()).catch(err =>
      logger.error('Failed to load integration packages', err),
    );
  }, [companyId, dispatch, isViewAllowed]);

  // The package list endpoint omits methods, so load them for the selected package.
  useEffectSafeStrictMode(() => {
    if (!selectedPackageId || !isViewAllowed) {
      return;
    }

    dispatch(getMethodsAction({ packageId: selectedPackageId })).catch(err =>
      logger.error('Failed to load integration methods', err),
    );
  }, [dispatch, selectedPackageId, isViewAllowed]);

  // The package list (summary) omits the per-env base URLs, so hydrate the full package record to know
  // which env slots are configured and can be offered in the env selector.
  useEffectSafeStrictMode(() => {
    if (!selectedPackageId || !isViewAllowed) {
      return;
    }

    dispatch(getPackageAction({ packageId: selectedPackageId })).catch(err =>
      logger.error('Failed to load integration package detail', err),
    );
  }, [dispatch, selectedPackageId, isViewAllowed]);

  const disabled = isDisabled || !isEditAllowed;

  const methodVerb = selectedMethod?.httpVerb?.toUpperCase() ?? null;
  const methodPath = selectedMethod?.pathTemplate ?? null;
  const environmentHelperText = getEnvHelperText(selectedPackageDetail);

  return (
    <Box className={wrapperClassname}>
      <AutocompleteCDS
        required
        OptionContent={PackageOptionContent}
        dataTestId={loanManagementTestIds.getIntegrationPackageSelectId(rootPath)}
        disabled={disabled || isPackagesLoading}
        getOptionLabel={getPackageLabel}
        isOptionEqualToValue={isPackageEqual}
        label="Integration Package"
        name={`${rootPath}.packageId`}
        options={packageOptions}
        value={selectedPackage}
        onChange={onPackageChange}
      />
      <Box>
        <AutocompleteCDS
          required
          OptionContent={MethodOptionContent}
          dataTestId={loanManagementTestIds.getIntegrationMethodSelectId(rootPath)}
          disabled={disabled || !selectedPackageId}
          getOptionLabel={getMethodLabel}
          isOptionEqualToValue={isMethodEqual}
          label="Method"
          name={`${rootPath}.methodId`}
          options={methods}
          value={selectedMethod}
          onChange={onMethodChange}
        />
        {!!methodVerb && !!methodPath && (
          <MethodEndpointBadge path={methodPath} verb={methodVerb} />
        )}
      </Box>
      <AutocompleteCDS
        dataTestId={loanManagementTestIds.getIntegrationEnvironmentSelectId(rootPath)}
        disabled={disabled || !selectedPackageId}
        getOptionDisabled={isEnvironmentOptionDisabled}
        getOptionLabel={getEnvironmentOptionLabel}
        helperText={environmentHelperText}
        isOptionEqualToValue={isEnvironmentOptionEqual}
        label="Vendor Environment"
        name={`${rootPath}.environment`}
        options={integrationEnvironmentOptions}
        placeholder="Inherit deployment"
        value={selectedEnvironment}
        onChange={onEnvironmentChange}
      />
    </Box>
  );
};

LoanActionIntegrationRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
};

export default memo(LoanActionIntegrationRow);
